import { Check, Folder } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SearchInput } from '@/components/ui/search-input';
import { Spinner } from '@/components/ui/spinner';

import { useFolders } from '../../hooks/useFolders';
import { filterOptionLabelClass } from './constants';

interface FolderFilterContentProps {
  onSelectFolder: (folderId: string, folderName: string) => void;
  selectedFolderId: string | null;
}

export function FolderFilterContent({
  onSelectFolder,
  selectedFolderId,
}: FolderFilterContentProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const { data: folders = [], error, isLoading } = useFolders({ enabled: true });

  const visibleFolders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return folders;
    }
    return folders.filter((f) => f.name.toLowerCase().includes(q));
  }, [folders, searchQuery]);

  return (
    <>
      <div className="px-3 py-2">
        <SearchInput
          onChange={setSearchQuery}
          placeholder={t('assistant.emailContext.searchFolders')}
          value={searchQuery}
        />
      </div>

      <div className="flex flex-col px-3 pb-3">
        {isLoading && (
          <div className="flex justify-center py-4">
            <Spinner className="text-neutral-mid size-5" />
          </div>
        )}
        {error && (
          <div className="text-destructive px-3 py-4 text-center text-sm">
            {t('assistant.emailContext.error')}
          </div>
        )}
        {!isLoading && !error && visibleFolders.length === 0 && (
          <div className="text-neutral-mid px-3 py-4 text-center text-sm">
            {t('assistant.emailContext.folderEmpty')}
          </div>
        )}
        {!isLoading &&
          !error &&
          visibleFolders.map((folder) => {
            const isSelected = folder.id === selectedFolderId;
            return (
              <button
                className={`hover:bg-back-bg2 flex w-full cursor-pointer items-center justify-between rounded-md px-2 py-2 transition-colors ${
                  isSelected ? 'bg-info-bg' : ''
                }`}
                key={folder.id}
                onClick={() => onSelectFolder(folder.id, folder.name)}
                type="button"
              >
                <span className="flex items-center gap-2">
                  <Folder className="text-neutral-mid size-3.5" />
                  <span className={filterOptionLabelClass}>{folder.name}</span>
                </span>
                {isSelected && <Check className="text-info size-3.5" />}
              </button>
            );
          })}
      </div>
    </>
  );
}
